import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { fetchInterventionsRequises } from '../redux/slices/demarchesSlice';
import { AppDispatch, RootState } from '../redux/store';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

const InterventionsRequises: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { demarches, loading } = useSelector((state: RootState) => state.demarches);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    dispatch(fetchInterventionsRequises());
  }, [dispatch]);

  const getClientName = (demarche: any) => {
    return demarche.type_client === 'entreprise'
      ? demarche.raison_sociale
      : `${demarche.client_nom} ${demarche.client_prenom || ''}`;
  };

  const getStatutColor = (statut: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
      'en_cours': 'info',
      'en_attente': 'warning',
      'termine': 'success',
      'rejete': 'error',
    };
    return colors[statut] || 'default';
  };

  // Composant carte pour mobile
  const InterventionCard = ({ demarche }: { demarche: any }) => (
    <Card
      sx={{
        mb: 2,
        borderLeft: 4,
        borderColor: 'warning.main',
        bgcolor: 'warning.50',
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          {/* Client */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Client
            </Typography>
            <Typography
              variant="body1"
              fontWeight={600}
              component={Link}
              to={`/clients/${demarche.client_id}`}
              sx={{
                textDecoration: 'none',
                color: 'primary.main',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {getClientName(demarche)}
            </Typography>
          </Box>

          <Divider />

          {/* Démarche */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Démarche
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {demarche.titre}
            </Typography>
            {demarche.description && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {demarche.description.substring(0, 100)}
                {demarche.description.length > 100 && '...'}
              </Typography>
            )}
          </Box>

          {/* Type et Statut */}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label={demarche.type_demarche_nom || 'Non spécifié'}
              size="small"
              color="info"
              variant="outlined"
            />
            <Chip
              label={demarche.statut}
              size="small"
              color={getStatutColor(demarche.statut)}
            />
          </Stack>

          {/* Date */}
          <Typography variant="caption" color="text.secondary">
            Déposée le: {demarche.date_depot || '-'}
          </Typography>

          {/* Bouton d'action */}
          <Button
            variant="contained"
            color="error"
            size="small"
            fullWidth
            startIcon={<WarningIcon />}
            onClick={() => navigate(`/demarches/${demarche.id}`)}
          >
            Intervenir
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight={700} sx={{ mb: 3 }}>
        {isMobile ? 'Interventions' : 'Interventions requises'}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Cette page affiche toutes les démarches marquées comme nécessitant une intervention immédiate.
      </Alert>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : !demarches || demarches.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={5}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Aucune intervention requise
              </Typography>
              <Typography color="text.secondary">
                Toutes vos démarches sont en cours normalement.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : isMobile ? (
        // Version mobile: Cartes
        <Box>
          {(demarches || []).map((demarche: any) => (
            <InterventionCard key={demarche.id} demarche={demarche} />
          ))}
        </Box>
      ) : (
        // Version desktop: Tableau
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Client</strong></TableCell>
                <TableCell><strong>Démarche</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Statut</strong></TableCell>
                <TableCell><strong>Date de dépôt</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(demarches || []).map((demarche: any) => (
                <TableRow
                  key={demarche.id}
                  sx={{
                    bgcolor: 'warning.50',
                    '&:hover': { bgcolor: 'warning.100' },
                  }}
                >
                  <TableCell>
                    <Typography
                      component={Link}
                      to={`/clients/${demarche.client_id}`}
                      sx={{
                        textDecoration: 'none',
                        color: 'primary.main',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {getClientName(demarche)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {demarche.titre}
                    </Typography>
                    {demarche.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {demarche.description.substring(0, 80)}
                        {demarche.description.length > 80 && '...'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={demarche.type_demarche_nom || 'Non spécifié'}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={demarche.statut}
                      size="small"
                      color={getStatutColor(demarche.statut)}
                    />
                  </TableCell>
                  <TableCell>{demarche.date_depot || '-'}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      startIcon={<WarningIcon />}
                      onClick={() => navigate(`/demarches/${demarche.id}`)}
                    >
                      Intervenir
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default InterventionsRequises;
